import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRestaurantGroup } from '../hooks/useRestaurantGroup';
import { GroupHero } from '../components/GroupHero';
import { GroupRestaurantCard } from '../components/GroupRestaurantCard';
import { safeColor } from '../utils/safeColor';
import { buildGroupThemeVars } from '../utils/groupTheme';
import { getAnalyticsEnvironment, ANALYTICS_ENV } from '@core/analytics/getEnvironment';
import { loadGTM } from '@core/analytics/scriptLoaders';

export function GroupLandingPage() {
  const { slug = '' } = useParams();
  const { t } = useTranslation('booking');
  const { data: group, isLoading, isError } = useRestaurantGroup(slug);

  // Load per-group GTM container once the group data arrives.
  // GroupAnalyticsProvider cannot do this because it mounts before the group
  // is fetched (above the router), so gtmKey is always undefined there.
  // loadGTM already guards against double-injection via a script-ID check.
  useEffect(() => {
    const gtmKey = group?.googleTagManagerKey;
    if (!gtmKey) return;
    try {
      if (getAnalyticsEnvironment() === ANALYTICS_ENV.PRODUCTION) {
        loadGTM(gtmKey);
      }
    } catch {
      // Analytics failure must never break the UI
    }
  }, [group?.googleTagManagerKey]);

  if (isLoading) {
    return <div data-testid="group-loading" className="min-h-screen" aria-busy="true" />;
  }
  if (isError || !group) {
    return (
      <div data-testid="group-not-found" className="min-h-screen flex items-center justify-center">
        <p>{t('GROUP_NOT_FOUND')}</p>
      </div>
    );
  }

  if (group.restaurants.length === 0) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <p data-testid="group-empty">{t('GROUP_NO_RESTAURANTS')}</p>
      </main>
    );
  }

  // ---------------------------------------------------------------------------
  // Group-driven theming via design-system CSS variables
  // ---------------------------------------------------------------------------

  // 1. Build CSS variable overrides from the group's configured colors.
  //    buildGroupThemeVars returns {} when no background color is configured,
  //    in which case the default design-system theme applies unchanged.
  //    Does NOT override --primary — the brand/CTA gold stays gold.
  const themeVars = buildGroupThemeVars(group);
  const isThemed = Object.keys(themeVars).length > 0;

  // 2. Background image: applied directly, no contrast risk.
  const bgImage = group.groupBackgroundImage || group.backgroundImage || '';

  // 3. Button theming: both bg and text must pass WCAG AA together.
  //    safeColor(buttonBgCandidate, buttonTextCandidate, fallback) checks that
  //    buttonBg renders buttonText readably. If either is missing or the pair
  //    fails WCAG AA, pass nothing to the card (it falls back to brand gold).
  const btnTextCandidate = group.groupButtonsTextColor || '#ffffff';
  const buttonBg = safeColor(group.groupButtonsBackgroundColor, btnTextCandidate, '');
  // Only pass buttonText when the bg is also safe — they must be applied together.
  const buttonText = buttonBg ? btnTextCandidate : '';

  // Build inline style: CSS var overrides + optional background image
  const pageStyle: React.CSSProperties = {
    ...themeVars,
    ...(bgImage ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
  };

  return (
    <main
      data-testid="group-landing"
      data-verify-restaurant-count={String(group.restaurants.length)}
      data-verify-themed={String(isThemed)}
      className="min-h-screen bg-background text-foreground"
      style={Object.keys(pageStyle).length > 0 ? pageStyle : undefined}
    >
      {/* Hero — logo + headline + language switcher */}
      <GroupHero
        logoImage={group.groupLogoImage || group.logoImage}
        groupSlug={group.slug}
      />

      {/* Restaurant grid — 1 col mobile → 2 cols sm → 3 cols lg */}
      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {group.restaurants.map((restaurant) => (
            <GroupRestaurantCard
              key={restaurant.id}
              restaurant={restaurant}
              {...(buttonBg ? { buttonBg } : {})}
              {...(buttonText ? { buttonText } : {})}
            />
          ))}
        </div>
      </section>
    </main>
  );
}
